// SAB-425: code-less public league join confirm page.
//
// Routed from /nfl/join/browse/:leagueId — the click-target from
// NFLLeaguesBrowsePage cards. Distinct from /nfl/join/:inviteCode (the
// invite-code path) so:
//   1. The URL structure makes the auth model obvious — leagueId in path
//      means visibility='public' is the gate, no code required.
//   2. The two flows can evolve independently without one breaking the
//      other.
//
// The actual join hits nfl-join-league with POST { league_id }; the edge
// function gates on visibility='public' before invoking the atomic RPC.
// INSUFFICIENT_FUNDS routes to the deposit flow scoped to this league_id,
// per the SAB-425 wallet model (per-(user, league)).
//
// Geo / state-eligibility (SAB-437) integration point is marked with a
// LABELED STUB below — when SAB-437 ships, it slots into the
// pre-confirm gate without changing the join flow shape.

import { useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { useLocanceGeoCheck, type GeoBlockReason } from '@/hooks/useLocanceGeoCheck'
import { GeoBlockScreen } from '@/components/locance/GeoBlockScreen'
import { GEO_ENABLED } from '@/lib/locance'
import { acquireGeoSession } from '@/lib/acquireGeoSession'
import { mapDepositError } from '@/lib/depositErrors'
import { eligibilityToGeoBlock } from '@/lib/geoEligibility'
import {
  PrizeScheduleDisclosure,
  type PrizeScheduleRow,
  type PrizeScheduleState,
} from '@/components/nfl/PrizeScheduleDisclosure'
import {
  Users, Gavel, DollarSign, Loader2, ChevronRight, AlertCircle,
  Check, LogIn, UserPlus, ArrowLeft,
} from 'lucide-react'

interface LeagueInfo {
  id: string
  name: string
  max_teams: number
  filled_teams: number
  entry_fee: number
  prize_pool: number | null
  prize_schedule_status: string | null
  draft_type: string | null
  status: string
  visibility: 'public' | 'private'
  commissioner_name: string
}

export default function NFLPublicJoinPage() {
  const { leagueId } = useParams<{ leagueId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { toast } = useToast()
  // SAB-466 Locance geo gate — FAIL CLOSED for paid buy-ins.
  const geo = useLocanceGeoCheck()
  // SAB-493: profile-eligibility block (declared state) → <GeoBlockScreen/>.
  const [eligibilityBlock, setEligibilityBlock] = useState<GeoBlockReason | null>(null)

  const [league, setLeague] = useState<LeagueInfo | null>(null)
  // SAB-632 pre-join disclosure block (from nfl_public_league_prize_schedules).
  const [schedule, setSchedule] = useState<{ state: PrizeScheduleState; rows: PrizeScheduleRow[] } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [teamName, setTeamName] = useState('')
  const [joining, setJoining] = useState(false)
  const [joined, setJoined] = useState(false)
  const [walletBalance, setWalletBalance] = useState<number | null>(null)
  const [insufficient, setInsufficient] = useState<{ required: number; available: number } | null>(null)

  useEffect(() => {
    if (!leagueId) return
    let cancelled = false

    async function load() {
      // Read league directly via supabase-js — same RLS path the browse
      // page uses. Faster than going through the edge function and avoids
      // the auth round-trip if the user isn't signed in yet.
      // Same nfl_teams FK disambiguation as NFLLeaguesBrowsePage — explicit
      // hint required because nfl_leagues.champion_team_id creates a
      // reverse FK path that confuses PostgREST's auto-resolver.
      // SAB security H3: read the safe public-browse view. It returns only public
      // leagues (private/nonexistent -> no row -> generic "not found", no existence
      // leak) and exposes commissioner_display_name instead of the raw commissioner_id.
      const { data: row, error: qErr } = await (supabase as any)
        .from('nfl_public_leagues')
        .select('id, name, max_teams, entry_fee, prize_pool, prize_schedule_status, draft_type, status, visibility, commissioner_display_name, filled_teams')
        .eq('id', leagueId)
        .single()
      if (cancelled) return
      if (qErr || !row) {
        setError('League not found.')
        setLoading(false)
        return
      }
      if ((row as any).visibility !== 'public') {
        // Don't leak existence of private leagues — same generic error.
        setError('League not found.')
        setLoading(false)
        return
      }
      const filled = Number((row as any).filled_teams ?? 0)
      setLeague({
        id: row.id,
        name: row.name,
        max_teams: row.max_teams,
        filled_teams: filled,
        entry_fee: Number(row.entry_fee ?? 0),
        prize_pool: row.prize_pool !== null && row.prize_pool !== undefined ? Number(row.prize_pool) : null,
        prize_schedule_status: (row as any).prize_schedule_status ?? null,
        draft_type: row.draft_type,
        status: row.status,
        visibility: (row as any).visibility,
        commissioner_name: (row as any).commissioner_display_name || 'Commissioner',
      })
      setLoading(false)

      // SAB-632 pre-join disclosure: payout structure from the public schedule view.
      // The view returns 'frozen' rows (verbatim league_prize_schedule dollars) or
      // 'provisional' percentage rows; zero rows on a locked league = finalizing.
      const { data: schedRows } = await (supabase as any)
        .from('nfl_public_league_prize_schedules')
        .select('state, label, final_rank, allocation_pct, resolved_amount, frozen_pool')
        .eq('league_id', leagueId)
      if (cancelled) return
      const rows = (schedRows ?? []) as Array<PrizeScheduleRow & { state: string }>
      if (rows.length > 0) {
        setSchedule({ state: rows[0].state === 'frozen' ? 'frozen' : 'provisional', rows })
      } else if ((row as any).prize_schedule_status || row.status !== 'pre_draft') {
        setSchedule({ state: 'finalizing', rows: [] })
      } else {
        setSchedule({ state: 'provisional', rows: [] })
      }
    }
    load()
    return () => { cancelled = true }
  }, [leagueId])

  // Look up wallet balance for this league (per-(user, league) model).
  useEffect(() => {
    if (!user || !league || league.entry_fee <= 0) return
    let cancelled = false
    async function loadBalance() {
      const { data } = await (supabase as any)
        .from('user_wallets')
        .select('balance')
        .eq('user_id', user!.id)
        .maybeSingle()
      if (cancelled) return
      setWalletBalance(Number(data?.balance ?? 0))
    }
    loadBalance()
    return () => { cancelled = true }
  }, [user, league])

  const handleJoin = async () => {
    if (!user || !league) return
    setJoining(true)
    setInsufficient(null)

    try {
      // SAB-466 (was the SAB-437 integration point): location-compliance gate
      // for PAID buy-ins, fired BEFORE confirm-and-pay. FAIL CLOSED — unlike
      // check-eligibility elsewhere, a Noncompliant/denied/error verdict blocks
      // the join. On a block we charge nothing and claim nothing. Free leagues
      // (entry_fee === 0) skip it — no money moves. Server verdict is
      // authoritative (locance-compliance-check); the SDK callback is only a
      // consent gate.
      let geoSessionId: string | undefined // SAB-600: forwarded to the server geo gate on a paid join.
      if (league.entry_fee > 0) {
        setEligibilityBlock(null)
        try {
          const { data: sess } = await supabase.auth.getSession()
          const eligRes = await fetch(
            `${(supabase as any).supabaseUrl}/functions/v1/check-eligibility`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${sess?.session?.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ action: 'get_user_status' }),
            }
          )
          const block = eligibilityToGeoBlock(await eligRes.json())
          if (block) {
            setEligibilityBlock(block)
            return
          }
        } catch {
          // fail open — a down eligibility service must not block
        }

        // Locance geo. Gather under GEO_ENABLED OR GEO_OBSERVE (see acquireGeoSession).
        // ok:false only under GEO_ENABLED (enforce client) on a non-Compliant verdict:
        // aborts here and geo.geoBlock drives the existing GeoBlockScreen overlay. Under observe we send
        // the session and let the SERVER decide (no local block, no new UI).
        const geoRes = await acquireGeoSession(geo)
        if (!geoRes.ok) {
          return
        }
        geoSessionId = geoRes.sessionId
      }

      const { data: session } = await supabase.auth.getSession()
      const token = session?.session?.access_token
      if (!token) {
        toast({ title: 'Not signed in', variant: 'destructive' })
        return
      }

      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/nfl-join-league`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            league_id: league.id,
            team_name: teamName.trim() || undefined,
            ...(geoSessionId ? { geo_session_id: geoSessionId } : {}),
          }),
        },
      )
      const data = await res.json()

      if (data.error === 'insufficient_funds' || data.code === 'INSUFFICIENT_FUNDS') {
        setInsufficient({
          required: Number(data.required ?? league.entry_fee),
          available: Number(data.available ?? 0),
        })
        return
      }
      if (data.error === 'already_joined') {
        toast({ title: "You're already in this league" })
        navigate('/nfl')
        return
      }
      if (data.error) {
        // TASK 1: shared money-action mapper (GEO_BLOCKED -> SAB-605 copy, KYC/RG map).
        // already_joined + insufficient_funds handled above.
        const { title, message } = mapDepositError({ code: data.code, message: data.error, category: data.category, detected_state: data.detected_state })
        toast({ title, description: message, variant: 'destructive' })
        return
      }
      setJoined(true)
      toast({ title: 'Joined!', description: `Welcome to ${data.league.name}` })
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' })
    } finally {
      setJoining(false)
    }
  }

  // ─── Loading / error states ─────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !league) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
        <Card className="bg-card border-border max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto" />
            <p className="text-sm text-foreground">{error || 'Something went wrong.'}</p>
            <Button onClick={() => navigate('/nfl/leagues/browse')} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Browse
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ─── Success ────────────────────────────────────────────────────────
  if (joined) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
        <Card className="bg-card border-border max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <div className="h-16 w-16 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto">
              <Check className="h-8 w-8 text-green-400" />
            </div>
            <h1 className="text-xl font-display font-bold text-foreground">
              Welcome to {league.name}!
            </h1>
            <p className="text-sm text-muted-foreground">
              You're in. Wait for the commissioner to start the draft.
            </p>
            <Button className="w-full bg-primary hover:bg-primary/90" onClick={() => navigate('/nfl')}>
              Go to League <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ─── Confirm screen ─────────────────────────────────────────────────
  const seatsLeft = league.max_teams - league.filled_teams
  const isFull = seatsLeft <= 0

  return (
    <div className="min-h-screen bg-gradient-dark px-4 py-8">
      {/* SAB-493 4c geo-block screen — profile-eligibility (declared) OR Locance
          (detected) layer for paid joins. Display-only; server gates authoritative. */}
      {(eligibilityBlock ?? (GEO_ENABLED ? geo.geoBlock : null)) && (
        <GeoBlockScreen
          reason={eligibilityBlock ?? (GEO_ENABLED ? geo.geoBlock : null)}
          busy={geo.state === 'prompting' || geo.state === 'checking'}
          onRetry={() => handleJoin()}
          onDismiss={() => {
            setEligibilityBlock(null)
            geo.reset()
          }}
        />
      )}
      <div className="max-w-md mx-auto space-y-4">
        <div className="text-center space-y-2">
          <div className="h-14 w-14 mx-auto rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
            <UserPlus className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-xl font-display font-bold text-foreground">Join Public League</h1>
        </div>

        <Card className="bg-card border-border">
          <CardContent className="p-4 space-y-4">
            <div>
              <h2 className="text-lg font-display font-bold text-foreground">{league.name}</h2>
              <p className="text-xs text-muted-foreground">
                Run by {league.commissioner_name}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Stat icon={<Users className="h-3.5 w-3.5" />} label="Teams" value={`${league.filled_teams}/${league.max_teams}`} />
              <Stat icon={<Gavel className="h-3.5 w-3.5" />} label="Draft" value={league.draft_type === 'snake' ? 'Live' : 'Slow'} />
              <Stat icon={<DollarSign className="h-3.5 w-3.5" />} label="Buy-in" value={league.entry_fee > 0 ? `$${league.entry_fee}` : 'Free'} />
            </div>

            {/* SAB-632 pre-join disclosure: payout structure, before the join CTA */}
            {schedule && (
              <PrizeScheduleDisclosure
                state={schedule.state}
                rows={schedule.rows}
                entryFee={league.entry_fee}
                prizePool={league.prize_pool}
              />
            )}

            <div className="flex items-center gap-2">
              {isFull ? (
                <Badge variant="outline" className="text-[10px] border-red-500/30 text-red-400">Full</Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] border-green-500/30 text-green-400">
                  {seatsLeft} {seatsLeft === 1 ? 'spot' : 'spots'} open
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Insufficient funds → route to deposit */}
        {insufficient && (
          <Card className="bg-card border-amber-500/30">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Not enough in your wallet</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    You have ${insufficient.available.toFixed(2)} in your wallet. Buy-in is ${insufficient.required.toFixed(2)}.
                    Deposit to continue.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate(`/beta?deposit=1&league_id=${league.id}&return=/nfl/join/browse/${league.id}`)}
                className="w-full bg-primary hover:bg-primary/90"
              >
                Add funds
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Join section */}
        {!isFull && !insufficient && !user && (
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-3">
              {/* SAB-592: sign-up leads (new invitee is the primary case); returnTo
                  carries the league through auth so they land back here signed-in. */}
              <p className="text-sm text-foreground font-semibold text-center">Create an account to join</p>
              <Button
                className="w-full bg-primary hover:bg-primary/90"
                onClick={() => navigate(`/signup?returnTo=${encodeURIComponent('/nfl/join/browse/' + league.id)}`)}
              >
                <LogIn className="h-4 w-4 mr-2" />
                Sign Up to Join
              </Button>
              <p className="text-[10px] text-muted-foreground text-center">
                Already have an account?{' '}
                <button onClick={() => navigate(`/login?returnTo=${encodeURIComponent('/nfl/join/browse/' + league.id)}`)} className="text-primary hover:underline">
                  Sign in
                </button>
              </p>
            </CardContent>
          </Card>
        )}

        {!isFull && !insufficient && user && (
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground">Your Team Name</label>
                <Input
                  placeholder="My Team"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  maxLength={30}
                  className="bg-muted/30"
                />
              </div>
              {league.entry_fee > 0 && walletBalance !== null && (
                <div className="text-[11px] text-muted-foreground p-2.5 rounded-lg bg-muted/30 border border-border">
                  Wallet balance for this league: <span className="font-bold text-foreground">${walletBalance.toFixed(2)}</span> · Buy-in: <span className="font-bold text-foreground">${league.entry_fee}</span>
                </div>
              )}
              <Button
                className="w-full bg-primary hover:bg-primary/90"
                onClick={handleJoin}
                disabled={joining}
              >
                {joining ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                {league.entry_fee > 0 ? `Join · $${league.entry_fee} buy-in` : 'Join'}
              </Button>
            </CardContent>
          </Card>
        )}

        <Link to="/nfl/leagues/browse" className="block text-center text-xs text-muted-foreground hover:text-foreground transition-colors">
          ← Back to public leagues
        </Link>
      </div>
    </div>
  )
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="p-2.5 rounded-lg bg-muted/30 border border-border text-center">
      <div className="flex items-center justify-center text-muted-foreground mb-1">{icon}</div>
      <p className="text-xs font-bold text-foreground">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  )
}
